import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Gift, Loader2, Share2, Smartphone } from "lucide-react";
import { toast } from "react-toastify";
import { getReferralStatus } from "../../services/ReferralService";
import { copyTextToClipboard, shareInviteOrCopy } from "../../utils/shareInvite";

const SHARE_TEXT =
  "I use DealHawk to find in-store clearance and local deals. Your first month is 50% off—and after your first successful payment, I earn 50% off a renewal too.";

export default function ReferralsTab() {
  const [copied, setCopied] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["referral-status"],
    queryFn: getReferralStatus,
    staleTime: 30_000,
  });

  const showCopied = (message: string) => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
    toast.success(message);
  };

  const selectInviteLink = () => {
    const input = document.getElementById("referral-invite-link") as HTMLInputElement | null;
    input?.focus();
    input?.select();
  };

  const copyInvite = async (successMessage = "Invite link copied") => {
    if (!data?.inviteUrl) return false;
    const didCopy = await copyTextToClipboard(data.inviteUrl);
    if (didCopy) {
      showCopied(successMessage);
      return true;
    }

    selectInviteLink();
    toast.error("Automatic copy was blocked. The link is selected—press ⌘C or Ctrl+C.");
    return false;
  };

  const shareInvite = async () => {
    if (!data?.inviteUrl) return;
    const result = await shareInviteOrCopy({
      title: "Try DealHawk",
      text: SHARE_TEXT,
      url: data.inviteUrl,
    });

    if (result === "shared") {
      toast.success("Invite shared");
      return;
    }
    if (result === "copied") {
      showCopied("Invite link copied—paste it into a message");
      return;
    }
    if (result === "failed") {
      selectInviteLink();
      toast.error("Sharing was blocked. The link is selected—press ⌘C or Ctrl+C.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-violet-600" aria-label="Loading referral rewards" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Could not load your referral rewards. Please try again.
      </div>
    );
  }

  const storeManaged = data.billingProvider === "apple" || data.billingProvider === "google";
  const hasReward = data.creditsBanked > 0;
  const rewardLabel = `${data.creditsBanked} half-price renewal ${data.creditsBanked === 1 ? "credit" : "credits"}`;

  let rewardHelp = "Your first 50%-off credit appears after a friend’s first successful payment.";
  if (data.pendingRedemptionId) {
    rewardHelp = "Your store is confirming 50% off your next monthly renewal.";
  } else if (storeManaged && hasReward) {
    rewardHelp = "Open the DealHawk mobile app to confirm the offer required by Apple or Google Play.";
  } else if (data.automatic && hasReward) {
    rewardHelp = "One credit is automatically applied to each upcoming monthly renewal.";
  }
  const statusLabel = storeManaged
    ? "Apply in mobile app"
    : data.automatic
      ? "Applied automatically"
      : "Available after subscribing";

  return (
    <div className="space-y-7 pb-2">
      <header>
        <h2 className="text-2xl font-black tracking-tight text-gray-950 dark:text-white sm:text-3xl">
          Invite &amp; earn
        </h2>
        <p className="mt-2 text-base text-gray-600 dark:text-dark-200">
          Give 50% off. Earn 50% off your next renewal.
        </p>
      </header>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-dark-700 dark:bg-dark-800 sm:p-7">
        <div className="grid gap-7 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-0">
          <ol className="space-y-5 lg:pr-9">
            <Step
              number="1"
              title="Share your link"
              body="Send your personal invite link to a friend."
            />
            <Step
              number="2"
              title="Your friend gets 50% off"
              body="Their first paid month starts at half price."
            />
            <Step
              number="3"
              title="You earn 50% off"
              body="One half-price renewal credit is banked after their first successful payment."
            />
          </ol>

          <div className="border-t border-gray-200 pt-7 dark:border-dark-700 lg:border-l lg:border-t-0 lg:pl-9 lg:pt-0">
            <label htmlFor="referral-invite-link" className="text-sm font-bold text-gray-900 dark:text-white">
              Your invite link
            </label>
            <div className="relative mt-2">
              <input
                id="referral-invite-link"
                readOnly
                value={data.inviteUrl}
                onFocus={(event) => event.currentTarget.select()}
                className="h-12 w-full truncate rounded-xl border border-gray-300 bg-white px-4 pr-12 text-sm text-gray-800 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 dark:border-dark-600 dark:bg-dark-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => void copyInvite()}
                aria-label={copied ? "Invite link copied" : "Copy invite link"}
                className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 dark:text-dark-200 dark:hover:bg-dark-700 dark:hover:text-violet-300"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>

            <button
              type="button"
              onClick={() => void shareInvite()}
              className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-violet-700 px-5 text-sm font-extrabold text-white transition hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 dark:bg-violet-600 dark:hover:bg-violet-500 dark:focus:ring-offset-dark-800"
            >
              <Share2 size={18} /> Share invite
            </button>

            <button
              type="button"
              onClick={() => void copyInvite()}
              className="mx-auto mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold text-violet-700 transition hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-violet-500 dark:text-violet-300 dark:hover:bg-violet-500/10"
            >
              {copied ? <Check size={17} /> : <Copy size={17} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-dark-700 dark:bg-dark-800 sm:p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
              <Gift size={20} />
            </div>
            <div className="min-w-0">
              <div className="text-2xl font-black tracking-tight text-gray-950 dark:text-white">{rewardLabel}</div>
              <p className="mt-1 text-sm text-gray-600 dark:text-dark-200">{rewardHelp}</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 dark:border-dark-700 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0">
            <span className="text-sm text-gray-600 dark:text-dark-200">Earned this year </span>
            <span className="text-sm font-extrabold text-gray-950 dark:text-white">
              {data.earnedThisYear} of {data.annualLimit}
            </span>
          </div>

          <div className="inline-flex h-11 flex-none items-center justify-center gap-2 rounded-xl bg-violet-50 px-4 text-sm font-extrabold text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
            {storeManaged ? <Smartphone size={17} /> : data.automatic ? <Check size={17} /> : <Gift size={17} />}
            {statusLabel}
          </div>
        </div>
      </section>

      <p className="border-t border-gray-200 pt-5 text-xs leading-5 text-gray-500 dark:border-dark-700 dark:text-dark-300">
        Friends receive 50% off their first paid month. You receive one 50%-off monthly renewal credit after their first successful payment. Credits are used one per renewal and you can earn up to {data.annualLimit} per calendar year. Apple and Google Play require offer confirmation in the mobile app. Rewards have no cash value, and self-referrals or duplicate payments do not qualify.
      </p>
    </div>
  );
}

function Step({ number, title, body }: { number: string; title: string; body: string }) {
  return (
    <li className="flex gap-4">
      <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-violet-100 text-xs font-black text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
        {number}
      </div>
      <div className="pt-0.5">
        <h3 className="text-sm font-extrabold text-gray-950 dark:text-white sm:text-base">{title}</h3>
        <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-dark-200">{body}</p>
      </div>
    </li>
  );
}
